import { Chip, Typography } from '@mui/material';
import moment from "moment";
import {
  AutocompleteArrayInput,
  ChipField,
  Datagrid,
  DateField,
  FunctionField,
  NumberField,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  SelectInput,
  SingleFieldList,
  TextField,
  useListContext,
  useTranslate,
} from 'react-admin';
import { Permission, ReportList } from "../../components";
import { BetweenDatesInput } from "../../components/BetweenDatesInput";
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from "../../constants";
import { filterToQuery } from "../../utils";

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold'
};

const statusChoices = [
  { id: 'pending_search', name: 'Aguardando Busca' },
  { id: 'pending_details', name: 'Aguardando Detalhes' },
  { id: 'completed', name: 'Concluído' },
  { id: 'failed', name: 'Falhou' },
  { id: 'invalid_payment', name: 'Pagamento Inválido' },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'pending_search':
    case 'pending_details':
      return 'warning';
    case 'failed':
    case 'invalid_payment':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  const choice = statusChoices.find(c => c.id === status);
  return choice?.name || status;
};

const ListQualityPaymentValidation = () => {
  const props = useListContext();

  let data = [], summaryData = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo) {
      data = reportInfo.data;
      summaryData = [reportInfo.summaryData];
    }
  }

  return (
    <>
      <Typography sx={titleSx} variant="h6">Resumo</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={1}>
        <NumberField
          source="total"
          label="Total"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
        <NumberField
          source="pending"
          label="Pendentes"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
        <NumberField
          source="completed"
          label="Concluídos"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
        <NumberField
          source="failed"
          label="Falharam"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
        <NumberField
          source="invalid_payment"
          label="Pagamento Inválido"
          textAlign="center"
          emptyText="0"
          sortable={false}
        />
      </Datagrid>
      <Typography sx={titleSx} variant="h6">Validações de Pagamento Quality</Typography>
      <Datagrid
        bulkActionButtons={false}
        resource={props.resource}
        data={data}
      >
        <FunctionField
          source="status"
          label="Status"
          render={(record: any) => (
            <Chip
              label={getStatusLabel(record?.status)}
              color={getStatusColor(record?.status) as any}
              size="small"
            />
          )}
        />
        <TextField source="fillinId" label="Fillin ID" />
        <ReferenceField
          label="Rede"
          emptyText="-"
          source="chainId"
          reference="chains"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Posto"
          emptyText="-"
          source="placeId"
          reference="places"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <NumberField
          source="estimatedPoints"
          label="Pontos Estimados"
          textAlign="center"
          emptyText="-"
        />
        <TextField source="paymentType" label="Forma Pagamento" emptyText="-" />
        <NumberField
          source="attemptCount"
          label="Tentativas"
          textAlign="center"
        />
        <DateField
          source="nextRetryAt"
          label="Próximo Retry"
          showTime
          emptyText="-"
          locales="pt-BR"
        />
        <DateField
          source="createdAt"
          label="Criado em"
          showTime
          locales="pt-BR"
        />
        <DateField
          source="completedAt"
          label="Concluído em"
          showTime
          emptyText="-"
          locales="pt-BR"
        />
        <TextField source="errorMessage" label="Erro" emptyText="-" />
      </Datagrid>
    </>
  );
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
      <ReferenceArrayField source="placeIds" emptyText="Todos" reference="stations" label="Posto" sortable={false}>
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
    </Datagrid>
  );
};

const ReportQualityPaymentValidation = (props: any) => {
  const translate = useTranslate();

  const listFilters = [
    <BetweenDatesInput alwaysOn />,
    <SelectInput
      source="status"
      label="Status"
      choices={statusChoices}
      alwaysOn
    />,
    <ReferenceArrayInput
      source="placeIds"
      reference="stations"
      perPage={null}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteArrayInput
        filterToQuery={filterToQuery}
        label="Posto"
        style={{ minWidth: 180 }}
        optionText={"name"}
        noOptionsText={translate('ra.input.autocomplete.no_options_text')}
      />
    </ReferenceArrayInput>
  ];

  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <ReportList periodRequired
        {...props}
        basePath="report-quality-payment-validation"
        resource="chains/chainId/reports/quality-payment-validation"
        title="Validação Quality"
        filters={listFilters}
        perPage={25}
        pagination={<ListPagination />}
        sort={{ field: 'createdAt', order: 'DESC' }}
        filterDefaultValues={{
          from: moment().startOf("day").subtract(7, "days").toISOString(),
          to: moment().endOf("day").toISOString(),
        }}
        titleOnPrint="Validação Quality"
        filterTablePrint={<FilterTablePrint />}
      >
        <ListQualityPaymentValidation />
      </ReportList>
    </Permission>
  );
};

export default ReportQualityPaymentValidation;
